import { createContext } from 'react';
import axios from 'axios';

// The manual library, on the server, shared by every admin.
//
// It used to be IndexedDB: the bytes never left the machine, so "your history" meant
// "this browser's history". Signing in on another laptop showed an empty library, and
// another admin on the SAME laptop saw yours — the store was never keyed by user, and
// the account only ever gated the door.
//
// The id is the file's SHA-256, assigned by the server (see notice_library.py). It
// used to be a random UUID de-duped by hash, which gave the same PDF a stable id on
// ONE device and a different one on every other — two admins uploading one manual
// could never have converged on one document. Everything keyed by docId — every
// phase's answers, the resume point — now means the same thing to both of them.

export interface NoticeFile {
  id: string;
  name: string;
  type: string;
  size: number;
  addedAt: number; // epoch ms
  data: Blob;
  // Cached identity from the run answers (server-derived). Absent until a manual
  // has been processed; the friendly /view/<slug> URL and history grouping use them.
  brand?: string | null;
  itemName?: string | null;
  category?: string | null;
  slug?: string | null;
}

// Lightweight view for the library list — everything but the blob bytes.
export type NoticeFileMeta = Omit<NoticeFile, 'data'>;

// Upload a manual and get back its record. The server hashes the bytes and returns
// the existing row if it has them already, so re-uploading a PDF — even renamed, even
// by the other admin — adopts the document and its answers rather than making a
// second copy.
export async function addFile(file: File): Promise<NoticeFile> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axios.post<{ id: string; name: string; type: string; size: number }>(
    '/api/notice/files', form,
  );
  return {
    id: data.id,
    name: data.name,
    type: data.type,
    size: data.size,
    addedAt: Date.now(),
    data: file,
  };
}

// A FOLDER: a document with no manual, for a product somebody has a 3D model of and no PDF
// (see create_folder). Born with a run and its brand and name; told apart by its type.
export const FOLDER_MIME = 'application/x-noticeia-folder';
export const isFolder = (f: Pick<NoticeFileMeta, 'type'> | null | undefined) => f?.type === FOLDER_MIME;

// WHETHER THE DOCUMENT ON SCREEN IS ONE, for what is deep in a phase (the import makes parts in a
// folder, whose objects are the only parts list it has). Provided by the viewer.
export const FolderDoc = createContext(false);

export async function createFolder(brand: string, name: string): Promise<string> {
  const { data } = await axios.post<{ id: string }>('/api/notice/files/folder', { brand, name });
  return data.id;
}

// One manual's bytes. Undefined when it is not there — deleted by whoever, from
// wherever, which is a thing that can happen now.
export async function getFile(id: string): Promise<NoticeFile | undefined> {
  try {
    const [meta, res] = await Promise.all([
      listFiles().then((all) => all.find((f) => f.id === id)),
      axios.get<Blob>(`/api/notice/files/${encodeURIComponent(id)}`, { responseType: 'blob' }),
    ]);
    if (!meta) return undefined;
    return { ...meta, data: res.data };
  } catch {
    return undefined;
  }
}

// Map a /view/<token> URL token — a friendly slug OR a raw SHA id — to the
// manual's canonical id + slug. Lets a slug deep link find its document, while
// old SHA links and the QR route keep resolving. Undefined when nothing matches.
export async function resolveDoc(
  token: string,
): Promise<{ id: string; slug: string | null } | undefined> {
  try {
    const { data } = await axios.get<{ id: string; slug: string | null }>(
      `/api/notice/files/resolve/${encodeURIComponent(token)}`,
    );
    return data;
  } catch {
    return undefined;
  }
}

// Library listing, newest first, without pulling blob bytes into the result.
export async function listFiles(): Promise<NoticeFileMeta[]> {
  try {
    const { data } = await axios.get<{ files: NoticeFileMeta[] }>('/api/notice/files');
    return data.files || [];
  } catch {
    return [];
  }
}

// Remove a manual and, with it, every answer any phase ever gave about it. The server
// deletes both: the answers are keyed by the id, and the id is the bytes — left
// behind they would be unreachable until the day someone re-uploaded the same PDF and
// silently inherited them.
export async function deleteFile(id: string): Promise<void> {
  await axios.delete(`/api/notice/files/${encodeURIComponent(id)}`);
}
